import _ from 'lodash';

/** 与 `MenuLibraryManagementState` 中 `MenuLibraryDisplayLang` 一致，避免与 state 循环依赖 */
export type MenuLibraryDisplayLang = 'unspecified' | 'zh' | 'en' | 'it' | 'sp' | 'fr' | 'de' | 'pt' | 'sl' | 'ca';

/**
 * 与 RBP2 `defaultI18n`（`dish-base-data.ts`）结构对齐的一级菜单名称多语言模板，解析 JSON 时 merge 以兼容缺省语言键。
 */
const DEFAULT_MAIN_GROUP_I18N_TEMPLATE: Record<string, { name1: string; name2: string; remark: string; extra_remark: string }> =
    {
        EN: { name1: '', name2: '', remark: '', extra_remark: '' },
        ZH: { name1: '', name2: '', remark: '', extra_remark: '' },
        IT: { name1: '', name2: '', remark: '', extra_remark: '' },
        ES: { name1: '', name2: '', remark: '', extra_remark: '' },
        FR: { name1: '', name2: '', remark: '', extra_remark: '' },
        DE: { name1: '', name2: '', remark: '', extra_remark: '' },
        PT: { name1: '', name2: '', remark: '', extra_remark: '' },
        SL: { name1: '', name2: '', remark: '', extra_remark: '' },
        CA: { name1: '', name2: '', remark: '', extra_remark: '' },
        default: { name1: '', name2: '', remark: '', extra_remark: '' },
    };

export type MainGroupNameI18nParsed = typeof DEFAULT_MAIN_GROUP_I18N_TEMPLATE;

/**
 * 解析 `main_group_name_i18n` JSON 字符串，与模板 merge（同 RBP2 `parseI18n` 语义）。
 * @param raw 后端返回的 JSON 字符串，可为空
 */
export function parseMainGroupNameI18n(raw: string | undefined | null): MainGroupNameI18nParsed {
    try {
        const parsed = raw && String(raw).trim() !== '' ? JSON.parse(String(raw)) : {};
        return _.merge({}, _.cloneDeep(DEFAULT_MAIN_GROUP_I18N_TEMPLATE), parsed);
    } catch {
        return _.cloneDeep(DEFAULT_MAIN_GROUP_I18N_TEMPLATE);
    }
}

/** Shell2 展示语言 → RBP2 JSON 语言码（一级菜单名称 `name1` 槽） */
const DISPLAY_LANG_TO_API: Record<Exclude<MenuLibraryDisplayLang, 'unspecified'>, keyof MainGroupNameI18nParsed> = {
    zh: 'ZH',
    en: 'EN',
    it: 'IT',
    sp: 'ES',
    fr: 'FR',
    de: 'DE',
    pt: 'PT',
    sl: 'SL',
    ca: 'CA',
};

/**
 * @param lang 当前编辑语言
 * @returns RBP2 JSON 中的语言键；未指定时返回 null（使用主字段 `main_group_name`）
 */
export function menuLibraryDisplayLangToI18nApiKey(
    lang: MenuLibraryDisplayLang,
): keyof MainGroupNameI18nParsed | null {
    if (lang === 'unspecified') {
        return null;
    }
    return DISPLAY_LANG_TO_API[lang];
}

export type MainGroupNameSlotInput = {
    /** 主名称 `main_group_name` */
    primary: string;
    /** `main_group_name_i18n` 原始字符串 */
    i18nJson: string;
    /** 当前编辑语言 */
    lang: MenuLibraryDisplayLang;
};

/**
 * 读取当前语言槽下展示的一级菜单名称（与 RBP2 `first-menu-detail` 展示逻辑一致）。
 */
export function getMainGroupNameForDisplay(input: MainGroupNameSlotInput): string {
    const { primary, i18nJson, lang } = input;
    const key = menuLibraryDisplayLangToI18nApiKey(lang);
    if (key == null) {
        return primary;
    }
    const parsed = parseMainGroupNameI18n(i18nJson);
    return String(_.get(parsed, [key, 'name1'], '') ?? '');
}

export type MainGroupNameSlotWriteInput = MainGroupNameSlotInput & {
    /** 输入框新值 */
    value: string;
};

/**
 * 写入当前语言槽并返回更新后的 `{ primary, i18nJson }`（不可变更新）。
 */
export function applyMainGroupNameInput(input: MainGroupNameSlotWriteInput): { primary: string; i18nJson: string } {
    const { primary, i18nJson, lang, value } = input;
    const key = menuLibraryDisplayLangToI18nApiKey(lang);
    if (key == null) {
        return { primary: value, i18nJson };
    }
    const parsed = parseMainGroupNameI18n(i18nJson);
    _.set(parsed, [key, 'name1'], value);
    return { primary, i18nJson: JSON.stringify(parsed) };
}

/**
 * 提交前规范化 i18n 字符串（merge 后再序列化，避免缺键）。
 * @param i18nJson 草稿 JSON 字符串
 */
export function normalizeMainGroupNameI18nJson(i18nJson: string): string {
    return JSON.stringify(parseMainGroupNameI18n(i18nJson));
}

/**
 * 与 `normalizeMainGroupNameI18nJson('')` 等价：merge 模板后的多语言 JSON 字符串，作草稿初始值（避免存裸 `''`，与 RBP2 `parseI18n` 空数据语义一致）。
 */
export const DEFAULT_MAIN_GROUP_NAME_I18N_JSON = normalizeMainGroupNameI18nJson('');
